import React, { useCallback, useMemo } from "react"
import ReactMarkdown from "react-markdown"

import { useAgentsById } from "@/agents/catalog"
import { useGetMediaPreviewUrlTransport } from "@/test-set/transport"
import { Badge } from "@coval/ui"
import { ImageIcon } from "lucide-react"

import { SIMULATION_INPUT_VIEW_MODE } from "@/lib/viewModes"
import { ExpandableTemplateVariableText } from "@/components/shared/ExpandableTemplateVariableText"
import { SimulatorType } from "@/types/api/SimulatorType"
import { isTestSetType, TestSetType } from "@/types/TestSetInput"

import { useFindReplaceContext } from "../../../contexts/FindReplaceContext"
import { useTestSetEditorContext } from "../../../contexts/TestSetEditorContext"
import type { EditableTestCase } from "../../../model"
import { getHighlightState } from "../../../utils/findReplaceHighlight"
import MediaAttachmentSection, {
  type LocalMediaAttachment,
} from "../../MediaAttachmentSection"
import { markdownComponents, normalizeMarkdown } from "./MarkdownComponents"
import { parseTranscriptTurns } from "./parseTranscriptTurns"
import { TextCell } from "./TextCell"
import { TranscriptReadView } from "./TranscriptReadView"

interface DefaultInputCellProps {
  testCase: EditableTestCase
  isExpanded: boolean
  isMarkedForDeletion: boolean
  onUpdate: (updates: Partial<EditableTestCase>) => void
  width: string
}

/** Text-based input cell (scenario/transcript) with template variables, find/replace highlighting, and inline diff for pending edits. */
export const DefaultInputCell: React.FC<DefaultInputCellProps> = ({
  testCase,
  isExpanded,
  isMarkedForDeletion,
  onUpdate,
  width,
}) => {
  const {
    activeAgentId,
    simulationInputViewMode,
    getLocalMediaAttachment,
    setLocalMediaAttachment,
    featureFlags,
  } = useTestSetEditorContext()
  const { findReplaceState } = useFindReplaceContext()
  const { agentsById } = useAgentsById()

  const activeAgent = useMemo(() => {
    if (activeAgentId && agentsById[activeAgentId]) {
      return agentsById[activeAgentId]
    }
    return null
  }, [activeAgentId, agentsById])

  const entityAttributes = useMemo(
    () => ({
      test_case: Object.keys(testCase.expected_output_json || {}),
      agent: activeAgent ? Object.keys(activeAgent.attributes || {}) : [],
    }),
    [testCase.expected_output_json, activeAgent],
  )

  const entityValues = useMemo(
    () => ({
      agent: activeAgent?.attributes || {},
      test_case: testCase.expected_output_json || {},
    }),
    [activeAgent, testCase.expected_output_json],
  )

  const highlightInfo = useMemo(
    () => getHighlightState(findReplaceState, testCase.id, "input_str"),
    [findReplaceState, testCase.id],
  )

  const localMediaAttachment = getLocalMediaAttachment(testCase.id)
  const persistedAttachment = testCase.media_attachments?.[0]
  const hasMediaAttachment =
    !!localMediaAttachment || (testCase.media_attachments?.length ?? 0) > 0
  const supportsMediaAttachments =
    activeAgent?.model_type === SimulatorType.WEBSOCKET
  const shouldShowMediaSection =
    featureFlags.isImageTestSetEnabled &&
    (supportsMediaAttachments || hasMediaAttachment)

  const { data: persistedPreviewData } = useGetMediaPreviewUrlTransport(
    {
      testCaseId: testCase.id,
      // Included for cache-busting when the persisted attachment changes.
      s3Key: persistedAttachment?.s3_key ?? null,
    },
    {
      enabled:
        isExpanded &&
        shouldShowMediaSection &&
        !!persistedAttachment?.s3_bucket &&
        !!persistedAttachment?.s3_key &&
        !localMediaAttachment,
      staleTime: 10 * 60 * 1000,
      retry: false,
    },
  )

  const handleMediaAttachmentChange = useCallback(
    (attachment: LocalMediaAttachment | null) => {
      setLocalMediaAttachment(testCase.id, attachment)
      // Do not persist partial S3 refs on row updates. Real media metadata is
      // generated after upload during save in TestSetHeader.
      if (!attachment && !persistedAttachment) {
        onUpdate({ media_attachments: undefined })
      }
    },
    [setLocalMediaAttachment, testCase.id, persistedAttachment, onUpdate],
  )

  const handlePersistedAttachmentChange = useCallback(
    (updates: { name: string; description?: string } | null) => {
      if (!persistedAttachment) {
        return
      }

      if (!updates) {
        onUpdate({ media_attachments: undefined })
        setLocalMediaAttachment(testCase.id, null)
        return
      }

      onUpdate({
        media_attachments: [
          {
            ...persistedAttachment,
            name: updates.name,
            description: updates.description,
          },
        ],
      })
    },
    [persistedAttachment, onUpdate, setLocalMediaAttachment, testCase.id],
  )

  const mediaIndicator = useMemo(() => {
    const hasAttachment =
      featureFlags.isImageTestSetEnabled && hasMediaAttachment

    if (!hasAttachment) {
      return null
    }

    return (
      <Badge size="status" variant="success" className="mt-1">
        <ImageIcon className="w-3 h-3" />
        {localMediaAttachment?.name ??
          testCase.media_attachments?.[0]?.name ??
          "Image"}
      </Badge>
    )
  }, [
    hasMediaAttachment,
    featureFlags.isImageTestSetEnabled,
    testCase.media_attachments,
    localMediaAttachment,
  ])

  const placeholder = "You are calling to get more information about ..."

  const isTranscript =
    isTestSetType(testCase.input_type) &&
    testCase.input_type === TestSetType.TRANSCRIPT

  return (
    <TextCell
      testCase={testCase}
      field="input_str"
      width={width}
      isExpanded={isExpanded}
      onUpdate={onUpdate}
      placeholder={placeholder}
      ariaLabel="Edit test case"
      cellRole="simulation-input"
      isMarkedForDeletion={isMarkedForDeletion}
      renderEdit={(value, onChange) => (
        <div>
          <ExpandableTemplateVariableText
            value={value}
            onChange={onChange}
            isExpanded
            autoFocus
            placeholder={placeholder}
            entityAttributes={entityAttributes}
            entityValues={entityValues}
            className="text-sm text-coval-text min-h-[100px]"
            highlightText={highlightInfo.highlightText}
            isHighlighted={highlightInfo.isCurrentMatch}
          />
          {shouldShowMediaSection && (
            <div className="mt-3">
              <MediaAttachmentSection
                localAttachment={localMediaAttachment}
                persistedAttachment={persistedAttachment || null}
                persistedPreviewUrl={persistedPreviewData?.url || null}
                canUpload={supportsMediaAttachments}
                onAttachmentChange={handleMediaAttachmentChange}
                onPersistedAttachmentChange={handlePersistedAttachmentChange}
              />
            </div>
          )}
        </div>
      )}
      renderReadOnly={(value, _isExpanded, isDeletion) => {
        if (isTranscript && value) {
          const turns = parseTranscriptTurns(value)
          if (turns) {
            return (
              <div>
                <TranscriptReadView
                  turns={turns}
                  isDeletion={isDeletion}
                  highlightText={highlightInfo.highlightText}
                  isCurrentMatch={highlightInfo.isCurrentMatch}
                />
                {mediaIndicator}
              </div>
            )
          }
        }

        if (
          simulationInputViewMode === SIMULATION_INPUT_VIEW_MODE.MARKDOWN &&
          value
        ) {
          return (
            <div
              className={`text-sm text-coval-text max-w-none wrap-break-word ${isDeletion ? "opacity-50 line-through" : ""}`}
            >
              <ReactMarkdown components={markdownComponents}>
                {normalizeMarkdown(value)}
              </ReactMarkdown>
              {mediaIndicator}
            </div>
          )
        }

        return (
          <div className="text-sm whitespace-pre-wrap wrap-break-word">
            <ExpandableTemplateVariableText
              value={value}
              isExpanded={false}
              placeholder={placeholder}
              entityAttributes={entityAttributes}
              entityValues={entityValues}
              className={`text-sm ${
                isDeletion
                  ? "text-coval-text opacity-50 line-through"
                  : "text-coval-text"
              }`}
              maxCollapsedLength={Infinity}
              highlightText={highlightInfo.highlightText}
              isHighlighted={highlightInfo.isCurrentMatch}
            />
            {mediaIndicator}
          </div>
        )
      }}
    />
  )
}
